import {call, select, put, take, takeEvery, takeLeading, delay} from 'redux-saga/effects';
import userManager from '@/shared/utils/userManager';
import {domainRedirectUrl} from '@/shared/utils/domainRedirect.js';

export const _getAcrValues = (settings) => {
    const values = [];

    // preferredIdp
    if (settings.preferredIdp) {
        values.push(`idp:${escape(settings.preferredIdp)}`);
    }

    // tenant
    if (settings.tenant) {
        values.push(`tenant:${settings.tenant}`);
        values.push(`sdbTenant:${settings.tenant}`);
    } else {
        values.push(`tenant:${settings.portalIdentifier}`);
    }

    values.push(`portalIdentifier:${settings.portalIdentifier}`);

    // loginDomains
    if (settings.loginDomains) {
        values.push(`loginDomains:${settings.loginDomains}`);
    }

    // forceDomain
    if (settings.forceDomain) {
        values.push(`forceDomain:${settings.forceDomain}`);
    }

    // externalCss: per conventie de login-CSS van dit portaal en deze hostname,
    // van de API, zodat de loginpagina dezelfde per-domein-styles krijgt als de app
    if (settings.apiUrl && settings.portalIdentifier) {
        const loginCssUrl = `${settings.apiUrl}/logincss/${settings.portalIdentifier}/${window.location.hostname}`;

        values.push(`externalCss:${escape(loginCssUrl)}`);
    }

    // externalCss
    if (settings.replacementNameIdentifierClaim) {
        values.push(`replaceClaim:${escape(settings.replacementNameIdentifierClaim)}`);
    }

    // externalIdpSpecificAcrValues
    if (settings.acrValues) {
        for (const [key, value] of Object.entries(settings.acrValues)) {
            values.push(`${key}:${escape(value)}`);
        }
    }

    return values.join(' ');
};

// Waar de gebruiker na het inloggen heen moet. Niet /callback of
// /silent_renew: aca-signin-page rendert ook terwijl de callback nog verwerkt
// wordt - aca-app kiest die tak zodra `_signIn` omklapt - en het
// aca-signin-auto-redirect daarin dispatcht SIGNIN vanuit connectedCallback.
// Het pad dat dan wordt vastgelegd is /callback, en daar stuurde doSignedIn de
// gebruiker vervolgens naartoe: inloggen lukte, maar je bleef op /callback staan.
//
// Dat stond er al. Tot #6602 verdween het achter een fout: de derde callback
// faalde met `No state in response` en deed in de catch
// `window.location.href = window.location.origin`, wat je alsnog op de
// startpagina bracht. Toen die fout weg was, was die redding het ook.
const AUTH_PADEN = ['/callback', '/silent_renew'];

export function terugkeerUrl() {
    const pad = window.location.pathname;

    if (AUTH_PADEN.some((authPad) => pad.startsWith(authPad))) return '/';

    return pad + window.location.search;
}

function* doSignIn() {
    // Niet opnieuw beginnen terwijl de callback nog verwerkt wordt. Op
    // /callback is state.oidc.user nog leeg, dus zet aca-app `_signIn` op true
    // en rendert het <aca-signin-page> in plaats van de router-outlet - en het
    // aca-signin-auto-redirect daarin dispatcht SIGNIN vanuit
    // connectedCallback. SIGNIN staat op takeEvery, dus dat start een tweede
    // rit naar de STS.
    //
    // Wie die race wint bepaalt waar de gebruiker uitkomt. Wint
    // authenticate-callback, dan wordt de oorspronkelijke state gebruikt en kom
    // je op je deeplink uit. Wint deze, dan legt terugkeerUrl() `/` vast - want
    // je staat op /callback - en kom je op het dashboard uit.
    //
    // Productie heeft dit niet: daar deed een geslaagde callback nog
    // `window.location.href = action.url`, en die navigatie sloopte de pagina
    // voordat die tweede rit iets kon doen. Sinds #6566 blijft de pagina leven.
    //
    // Dit kan geen eerste login blokkeren: daarvoor sta je nooit op /callback.
    if (AUTH_PADEN.some((authPad) => window.location.pathname.startsWith(authPad))) return;

    const state = yield select();

    yield call([userManager, userManager.signinRedirect], {
        acr_values: _getAcrValues(state.minervaSettings),
        state: {url: terugkeerUrl()},
    });
}

function* doSignOut() {
    yield call([userManager, userManager.signoutRedirect]);
}

function* doSigninSilent() {
    const state = yield select();

    try {
        yield call([userManager, userManager.signinSilent], {
            acr_values: _getAcrValues(state.minervaSettings),
        });

        yield put({type: 'SIGNIN_SILENT_FULFILLED'});
    } catch (error) {
        yield put({type: 'SIGNIN_SILENT_REJECTED', error});
    }
}

function* doUserFound() {}

export function* signIn() {
    yield takeEvery('SIGNIN', doSignIn);
    yield takeLeading('SIGNINCALLBACK', doSignInCallback);
    yield takeEvery('SIGNEDIN', doSignedIn);
    yield takeEvery('SIGNINSILENT', doSigninSilent);

    yield takeEvery('redux-oidc/USER_FOUND', doUserFound);

    yield takeEvery('SIGNOUT', doSignOut);
}

// Eén verwerkte callback per paginalading. `authenticate-callback` dispatcht
// in firstUpdated, en dat element wordt tijdens het inloggen meermaals opnieuw
// aangemaakt: aca-app kiest in render() tussen `<aca-signin-page>` en de
// router-outlet op basis van `_signIn`, en die vlag klapt om bij elke
// store-wijziging. Elke omslag gooit de outlet weg en bouwt hem opnieuw op.
//
// Zolang een geslaagde callback de pagina meteen herlaadde viel dat niet op.
// Sinds #6566 navigeert hij client-side, dus blijven de latere exemplaren
// leven: gemeten vier keer CONSTRUCTOR, twee keer geslaagd en één keer
// `No state in response` - die laatste belandde in de catch hieronder en deed
// alsnog een volledige paginalading, waarmee de diepe link verloren ging.
let callbackHandled = false;

// Alleen voor de tests: een nieuwe paginalading geeft een nieuwe module.
export function _resetCallbackHandled() {
    callbackHandled = false;
}

function* doSignInCallback() {
    if (callbackHandled) return;

    // Zonder respons in de url valt er niets te verwerken. Dat is precies het
    // geval waarin signinRedirectCallback `No state in response` gooide.
    if (!/[#?].*(?:state=|code=|id_token=|error=)/.test(window.location.href)) {
        if (window.location.pathname.startsWith('/callback')) yield call(herstartLogin);

        return;
    }

    callbackHandled = true;

    try {
        const result = yield call([userManager, userManager.signinRedirectCallback]);

        yield call([userManager, userManager.clearStaleState]);

        yield put({type: 'SIGNEDIN', url: result.state.url});
    } catch (error) {
        console.error('Authenticate callback FAIL', error);
        window.location.href = window.location.origin;
    }

    // Debounce because state changes can trigger multiple
    // SIGNINCALLBACK actions.
    // Combines with takeLeading, so no new calls will
    // come in while this one is waiting
    yield delay(250);
}

// Op /callback zonder respons, zonder callback in behandeling, stond de app stil
// (#7364): doSignIn begint op /callback niet opnieuw. Zo kom je er bijvoorbeeld
// na een domein-redirect vanaf een oude host, die de hash met de respons niet
// meeneemt. Tot #6602 herstelde dat via de catch hierboven.
//
// Daarom naar `/` en opnieuw inloggen, met een SSO-sessie zonder dat de gebruiker
// iets merkt. Pas met de settings erbij, anders gaan de acr_values zonder portaal
// naar de STS. Stuurt aca-app deze host door, dan niet: die navigatie gaat voor.
//
// De url blijft /callback tot vlak voor de SIGNIN. De settings laten
// aca-signin-page een aca-signin-auto-redirect renderen, en dat dispatcht zelf
// SIGNIN; op /callback houdt doSignIn die tegen. De delay laat die render eerst
// gebeuren, anders gaan er twee logins naar de STS. Ook als de settings er al
// waren: een store-wijziging kan aca-signin-page dan nog opnieuw laten renderen. Toont het portaal een
// welkomstscherm, dan logt dit toch meteen in: de gebruiker kwam net van een login.
const settingsBinnen = (settings) =>
    !!(settings && (settings.portalIdentifier || settings.notFound || settings.forceLogout));

function* herstartLogin() {
    callbackHandled = true;

    let {minervaSettings} = yield select();

    if (!settingsBinnen(minervaSettings)) {
        yield take(['MINERVASETTINGS_RECEIVED', 'MINERVASETTINGS_NOT_FOUND']);

        ({minervaSettings} = yield select());
    }

    yield delay(0);

    // Een onbekend portaal of forceLogout: aca-app toont daar zijn eigen pagina.
    if (!minervaSettings?.portalIdentifier || minervaSettings.forceLogout) return;

    if (domainRedirectUrl(minervaSettings.redirects, window.location)) return;

    window.history.replaceState({}, '', '/');

    yield put({type: 'SIGNIN'});
}

function* doSignedIn(action) {
    yield put({type: 'MINERVASETTINGS_REQUESTED'});
    // TODO: find a way to updateUserDetails from here?
    // ////await dispatch(updateUserDetails());

    yield put({type: 'SIGNIN_FULFILLED'});

    // Zonder url valt er niets te navigeren. Sinds #6609 handelt de parent de
    // silent renew zelf af en komt hier niets meer vandaan, maar de guard blijft:
    // een SIGNEDIN zonder url mag nooit ergens heen sturen.
    if (!action.url) return;

    // Een client-side routewissel, en geen paginalading (#6566). Op dit punt
    // staat de gebruiker al in de store: oidc-client heeft userLoaded gevuurd
    // en aca-app heeft daarop de settings met token opgevraagd. Een reload
    // gooide dat weg en startte de hele applicatie een tweede keer op.
    //
    // Dit is het navigatie-idioom van de rest van de applicatie (zie
    // useNavigation.hook.ts en __routeTo in aca-app.js): iron-location vangt
    // location-changed op voor de Lit-router, useCustomLocation voor React.
    //
    // replaceState en niet pushState: anders blijft /callback#id_token=... in
    // de history staan en loopt Terug opnieuw de callback in, die dan faalt.
    // De state kan uit een oudere sessie komen en dan alsnog /callback
    // bevatten; die mag hier niet doorheen.
    const doel = AUTH_PADEN.some((authPad) => action.url.startsWith(authPad)) ? '/' : action.url;

    window.history.replaceState({}, '', doel);
    window.dispatchEvent(new CustomEvent('location-changed'));
}
